import React from 'react';

const SPECIAL_BLOCK = {
  'acciones sugeridas': 'actions',
  'acciones siguientes': 'actions',
  'próximos pasos': 'actions',
  'próximas acciones': 'actions',
  'acciones': 'actions',
  'riesgos': 'risks',
  'riesgos detectados': 'risks',
  'prioridades': 'priorities',
  'prioridad alta': 'priorities',
  'diagnóstico': 'diagnosis',
  'diagnostico': 'diagnosis',
  'next actions': 'actions',
  'suggested actions': 'actions',
  'detected risks': 'risks',
  'high priority': 'priorities',
};

const METRIC_RE =
  /(\d+(?:[.,]\d+)?\s*%|\$\s*[\d.,]+|[\d.,]+\s*(?:ARS|USD)(?:\b|$)|\d+\s*\/\s*\d+|\d+\s+(?:documentos?|licencias?|d[ií]as|meses|semanas|años|colaboradores?)\b)/gi;

/**
 * Resalta números y métricas simples en un segmento de texto plano.
 * @param {string} segment
 * @returns {React.ReactNode[]}
 */
function splitMetricsInPlain(segment) {
  if (!segment) return [];
  const parts = [];
  let last = 0;
  let m;
  const re = new RegExp(METRIC_RE.source, METRIC_RE.flags);
  while ((m = re.exec(segment)) !== null) {
    if (m.index > last) {
      parts.push(segment.slice(last, m.index));
    }
    parts.push(
      <span key={`met-${m.index}-${m[0].slice(0, 8)}`} className="flow-ia-metric">
        {m[0]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < segment.length) {
    parts.push(segment.slice(last));
  }
  return parts.length ? parts : [segment];
}

/**
 * Parsea **negrita** y métricas en una línea.
 * @param {string} line
 */
export function FlowIARichInline({ line }) {
  const s = String(line ?? '');
  if (!s) return null;
  const nodes = [];
  let i = 0;
  let k = 0;
  while (i < s.length) {
    const open = s.indexOf('**', i);
    if (open === -1) {
      nodes.push(<React.Fragment key={k++}>{splitMetricsInPlain(s.slice(i))}</React.Fragment>);
      break;
    }
    if (open > i) {
      nodes.push(<React.Fragment key={k++}>{splitMetricsInPlain(s.slice(i, open))}</React.Fragment>);
    }
    const close = s.indexOf('**', open + 2);
    if (close === -1) {
      nodes.push(<React.Fragment key={k++}>{splitMetricsInPlain(s.slice(open))}</React.Fragment>);
      break;
    }
    const bold = s.slice(open + 2, close);
    nodes.push(
      <strong key={k++} className="flow-ia-strong">
        {splitMetricsInPlain(bold)}
      </strong>
    );
    i = close + 2;
  }
  return <>{nodes}</>;
}

function groupLinesIntoBlocks(lines) {
  const blocks = [];
  let current = [];
  const flush = () => {
    if (current.length) {
      blocks.push(current);
      current = [];
    }
  };
  for (let li = 0; li < lines.length; li += 1) {
    const line = lines[li].trim();
    if (!line) {
      flush();
      continue;
    }
    if (/^([-*_])\1{2,}$/.test(line)) {
      flush();
      blocks.push({ __hr: true });
      continue;
    }
    const isUl = /^[-*]\s/.test(line);
    const isOl = /^\d+\.\s/.test(line);
    const curFirst = current[0];
    const curType = !curFirst ? null : /^[-*]\s/.test(curFirst) ? 'ul' : /^\d+\.\s/.test(curFirst) ? 'ol' : 'text';
    const newType = isUl ? 'ul' : isOl ? 'ol' : 'text';
    if (current.length && curType !== newType && (curType === 'ul' || curType === 'ol' || newType === 'ul' || newType === 'ol')) {
      flush();
    }
    current.push(line);
  }
  flush();
  return blocks;
}

function stripListPrefix(line) {
  if (/^[-*]\s/.test(line)) return line.replace(/^[-*]\s+/, '');
  if (/^\d+\.\s/.test(line)) return line.replace(/^\d+\.\s+/, '');
  return line;
}

function classifyTextBlock(lines) {
  if (!lines.length) return { kind: 'empty' };
  const head = lines[0];
  const headKey = head.replace(/:\s*$/, '').trim().toLowerCase();
  if (lines.length > 1 && SPECIAL_BLOCK[headKey]) {
    return {
      kind: 'special',
      variant: SPECIAL_BLOCK[headKey],
      title: head.replace(/:\s*$/, ''),
      body: lines.slice(1),
    };
  }
  if (lines.length === 1 && /^#{1,3}\s+/.test(head)) {
    const m = head.match(/^#{1,3}\s+(.+)$/);
    const level = head.match(/^#+/)[0].length;
    return { kind: 'mdheading', level: Math.min(3, level), text: m ? m[1] : head };
  }
  if (lines.length === 1 && head.length < 100 && /:\s*$/.test(head) && !head.includes('. ')) {
    return { kind: 'heading', text: head.replace(/:\s*$/, '') };
  }
  return { kind: 'paragraph', lines };
}

function renderBodyLines(body) {
  const allUl = body.length > 0 && body.every((l) => /^[-*]\s/.test(l));
  const allOl = body.length > 0 && body.every((l) => /^\d+\.\s/.test(l));
  if (allUl) {
    return (
      <ul className="flow-ia-response__ul">
        {body.map((l, i) => (
          <li key={i}>
            <FlowIARichInline line={stripListPrefix(l)} />
          </li>
        ))}
      </ul>
    );
  }
  if (allOl) {
    return (
      <ol className="flow-ia-response__ol">
        {body.map((l, i) => (
          <li key={i}>
            <FlowIARichInline line={stripListPrefix(l)} />
          </li>
        ))}
      </ol>
    );
  }
  return body.map((l, i) => (
    <p key={i} className="flow-ia-response__p">
      <FlowIARichInline line={l} />
    </p>
  ));
}

/**
 * @param {{ text: string }} props
 */
export function FlowIAResponseRenderer({ text }) {
  const raw = String(text ?? '');
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const grouped = groupLinesIntoBlocks(lines);

  return (
    <div className="flow-ia-response">
      {grouped.map((block, idx) => {
        if (block && block.__hr) {
          return <hr key={`hr-${idx}`} className="flow-ia-response__sep" />;
        }
        if (!Array.isArray(block) || block.length === 0) return null;

        const first = block[0];
        if (/^[-*]\s/.test(first)) {
          return (
            <ul key={idx} className="flow-ia-response__ul">
              {block.map((l, i) => (
                <li key={i}>
                  <FlowIARichInline line={stripListPrefix(l)} />
                </li>
              ))}
            </ul>
          );
        }
        if (/^\d+\.\s/.test(first)) {
          return (
            <ol key={idx} className="flow-ia-response__ol">
              {block.map((l, i) => (
                <li key={i}>
                  <FlowIARichInline line={stripListPrefix(l)} />
                </li>
              ))}
            </ol>
          );
        }

        const cls = classifyTextBlock(block);
        if (cls.kind === 'special') {
          const v = cls.variant;
          const mod =
            v === 'actions' ? 'flow-ia-response__callout--actions' : v === 'risks' ? 'flow-ia-response__callout--risks' : v === 'priorities' ? 'flow-ia-response__callout--priorities' : 'flow-ia-response__callout--diagnosis';
          return (
            <div key={idx} className={`flow-ia-response__callout ${mod}`}>
              <h4 className="flow-ia-response__callout-title">{cls.title}</h4>
              {renderBodyLines(cls.body)}
            </div>
          );
        }
        if (cls.kind === 'mdheading') {
          const Tag = cls.level <= 1 ? 'h3' : 'h4';
          return (
            <Tag key={idx} className="flow-ia-response__subtitle">
              <FlowIARichInline line={cls.text} />
            </Tag>
          );
        }
        if (cls.kind === 'heading') {
          return (
            <h4 key={idx} className="flow-ia-response__subtitle flow-ia-response__subtitle--inline">
              <FlowIARichInline line={cls.text} />
            </h4>
          );
        }
        return (
          <div key={idx} className="flow-ia-response__block">
            {cls.lines.map((l, i) => (
              <p key={i} className="flow-ia-response__p">
                <FlowIARichInline line={l} />
              </p>
            ))}
          </div>
        );
      })}
    </div>
  );
}
